import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import { JsonEditor } from 'json-edit-react';
import { useSnackbar } from 'notistack';
import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useGetChargePoint, useUpdateChargePoint } from '../../../../../app/ApiGen';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { Field, List, ListItem, Loading, Section, Workspace } from '../../../../../design-system';
import { normalise } from '../../../../../utils/request';

function useChargePoint(id: string) {
  const { loading, data, error, refetch } = useGetChargePoint({ id });
  const chargePoint = useMemo(() => (data ? normalise(data) : undefined), [data]);
  const connectionConfiguration = useMemo(() => data?.data.attributes.connectionConfiguration, [data]);
  return { loading, error, refetch, chargePoint, connectionConfiguration };
}

export const ConnectionConfiguration: React.VFC = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();

  const { loading, error, refetch, connectionConfiguration } = useChargePoint(id);
  const { loading: updating, mutate } = useUpdateChargePoint({ id });

  const [isEnabled, setIsEnabled] = React.useState(false);

  React.useEffect(() => {
    setIsEnabled(connectionConfiguration?.automaticallyManaged ?? false);
  }, [connectionConfiguration]);

  const handleUpdate = React.useCallback(async () => {
    if (!connectionConfiguration) {
      return;
    }
    try {
      await mutate({
        data: {
          id,
          type: 'chargePoints',
          attributes: { connectionConfiguration: { automaticallyManaged: isEnabled } },
        },
      });
      enqueueSnackbar(`Automatic management ${isEnabled ? 'enabled' : 'disabled'}`, { variant: 'success' });
      await refetch();
    } catch (err) {
      errorSnackbar('Unable to update charge point', closeSnackbar, enqueueSnackbar, err);
    }
  }, [mutate, id, isEnabled, connectionConfiguration, enqueueSnackbar, refetch, closeSnackbar]);

  if (loading) {
    return (
      <Workspace>
        <Loading />
      </Workspace>
    );
  }

  if (error) {
    return <DataErrorHandler description="Unable to load charge point" error={error} refetch={refetch} />;
  }

  if (!connectionConfiguration) {
    return (
      <Workspace>
        <Section title="Connection configuration">
          <Card title="Connection configuration">
            <Box p={2}>No connection configuration available for this charge point.</Box>
          </Card>
        </Section>
      </Workspace>
    );
  }

  return (
    <Workspace>
      <Section title="Connection configuration">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <List>
                <ListItem>
                  <JsonEditor data={connectionConfiguration} restrictAdd restrictDelete restrictDrag restrictEdit />
                </ListItem>
              </List>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <Box>
                <List inset>
                  <ListItem>
                    <Field label="Automatically managed">
                      <FormControl>
                        <Switch
                          id="enabled"
                          color="primary"
                          checked={isEnabled}
                          onChange={({ target: { checked } }) => setIsEnabled(checked)}
                        />
                      </FormControl>
                    </Field>
                  </ListItem>
                </List>
                <Box p={4}>
                  <Button
                    color="primary"
                    disabled={updating}
                    onClick={() => {
                      handleUpdate().catch((err) => {
                        errorSnackbar('Unable to update charge point', closeSnackbar, enqueueSnackbar, err);
                      });
                    }}
                    variant="contained"
                  >
                    {updating ? <CircularProgress size={26} color="inherit" /> : 'Update'}
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Section>
    </Workspace>
  );
};
